import { createContext, useContext, useEffect, useState, useCallback } from "react";
import client from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = auth
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("iclean_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("iclean_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfaRequired: true, tempToken: data.temp_token };
    if (data.token) localStorage.setItem("iclean_token", data.token);
    setUser(data);
    return { mfaRequired: false, user: data };
  };

  const verifyMfa = async (tempToken, code) => {
    const { data } = await client.post("/auth/mfa/verify", { temp_token: tempToken, code });
    if (data.token) localStorage.setItem("iclean_token", data.token);
    setUser(data);
    return data;
  };

  const setupMfa = async () => (await client.get("/auth/mfa/setup")).data;
  const enableMfa = async (code) => {
    const { data } = await client.post("/auth/mfa/enable", { code });
    await loadMe();
    return data;
  };

  const logout = async () => {
    try { await client.post("/auth/logout"); } catch {}
    localStorage.removeItem("iclean_token");
    setUser(false);
  };

  const can = (perm) => !!user && Array.isArray(user.permissions) && user.permissions.includes(perm);
  const messagingEnabled = user ? user.messagingEnabled !== false : true;

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, setupMfa, enableMfa, logout, can, messagingEnabled, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
